/**
 * career-onboarding — the upload + extraction surface (route `/onboarding`).
 *
 * One upload affordance per DocumentKind + a portrait, each a real <button>
 * (data-act="upload-document") that opens a native file picker on click (AX-3:
 * click-only affordances) and uploads via the BFF multipart route. "Start
 * extraction" runs the ingest pipeline; the run's live status renders from the
 * push-fed ingest store (aria-live), with a link to the review once proposals land.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { I18nToken, IngestStoreToken, PushToken, RouterToken, SessionToken, apiGet, apiPost, apiUpload } from "../services"
import { createStreamClaim, type StreamClaim } from "../lib/push-streams"
import type { CareerI18n, CareerKey } from "../../i18n"
import { careerStream, ingestRunUrn, type IngestRunView, type IngestStore, type IngestStatus, type ShellSession } from "../domain"
import type { Router } from "@platform/web-kit"

/** The document kinds the onboarding collects (English codes; German labels via t()). */
const DOC_KINDS = ["cv", "letter", "reference", "certificate"] as const
type DocKind = (typeof DOC_KINDS)[number]

const KIND_LABEL: Record<DocKind, CareerKey> = {
  cv: "onboarding.kind.cv",
  letter: "onboarding.kind.letter",
  reference: "onboarding.kind.reference",
  certificate: "onboarding.kind.certificate",
}
const STATUS_KEY: Record<IngestStatus, CareerKey> = {
  pending: "onboarding.status.pending",
  extracting: "onboarding.status.extracting",
  proposed: "onboarding.status.proposed",
  confirmed: "onboarding.status.confirmed",
  failed: "onboarding.status.failed",
}

interface UploadedDoc {
  kind: string
  name: string
}

export class OnboardingVm implements ViewModel {
  readonly ingestId: Signal<string | null> = signal<string | null>(null)
  readonly uploaded: Signal<readonly UploadedDoc[]> = signal<readonly UploadedDoc[]>([])
  readonly busyKind: Signal<string | null> = signal<string | null>(null)
  readonly starting: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  /** This surface's claim on the shared push channel (`lib/push-streams.ts`): the channel
   *  subscription is the UNION of every mounted surface's claim, never just ours. */
  private readonly claim: StreamClaim

  constructor(
    private readonly store: IngestStore,
    push: PushChannel,
    private readonly router: Router,
    private readonly session: ShellSession,
    i18n: CareerI18n,
  ) {
    this.t = i18n.t
    this.claim = createStreamClaim(push)
    // The push-fed "aktiv" projection notifies on every run fold — the live rail.
    this.renderDeps = [this.ingestId, this.uploaded, this.busyKind, this.starting, this.error, this.store.list("aktiv")]
  }

  get runView(): IngestRunView | undefined {
    const id = this.ingestId.value
    if (!id) return undefined
    return this.store.entity(ingestRunUrn(id)).value
  }

  private get userId(): string | null {
    return this.session.user?.sub ?? null
  }

  /** Rehydrate the latest ingest run on mount (M2) so nav-away-and-back never blanks
   *  the surface — and a fresh upload reuses this run rather than spawning a second. */
  attach(): () => void {
    void this.resume()
    return () => this.claim.release()
  }

  private async resume(): Promise<void> {
    try {
      const runs = await apiGet<Array<{ id: string; status: string; startedAt?: string }>>("/api/ingest")
      if (runs.length === 0) return
      // Prefer the chronologically LATEST run (m8c) — a scanned-only latest run then shows the
      // honest zero-text state instead of resurfacing an earlier successful run.
      const latest = [...runs].sort((a, b) => (b.startedAt ?? "").localeCompare(a.startedAt ?? ""))[0]!
      const id = latest.id.replace(/^urn:career:ingest:/, "")
      this.ingestId.set(id)
      const detail = await apiGet<{ documents: Array<{ kind: string }> }>(`/api/ingest/${id}`)
      this.uploaded.set(detail.documents.map((d) => ({ kind: d.kind, name: "" })))
      const userId = this.userId
      if (userId) this.claim.claim([careerStream(userId, "Ingest", ingestRunUrn(id))])
    } catch {
      // No prior run (or read failed) — start fresh; the surface renders its empty state.
    }
  }

  async pickAndUpload(kind: string, portrait = false): Promise<void> {
    const input = document.createElement("input")
    input.type = "file"
    // The formats the parser actually reads (PDF/DOCX now land via real extractors).
    input.accept = portrait ? "image/*" : ".md,.txt,.pdf,.docx"
    input.onchange = () => {
      const file = input.files?.[0]
      if (file) void this.upload(kind, file, portrait)
    }
    input.click()
  }

  private async upload(kind: string, file: File, portrait: boolean): Promise<void> {
    this.busyKind.set(kind)
    this.error.set(null)
    try {
      const form = new FormData()
      form.set("file", file)
      if (portrait) form.set("portrait", "true")
      else form.set("kind", kind)
      if (this.ingestId.value) form.set("ingestId", this.ingestId.value)
      const res = await apiUpload<{ ingestId?: string }>("/api/documents/upload", form)
      if (res.ingestId) this.ingestId.set(res.ingestId)
      this.uploaded.update((list) => [...list, { kind, name: file.name }])
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busyKind.set(null)
    }
  }

  async startExtraction(): Promise<void> {
    const id = this.ingestId.value
    const userId = this.userId
    if (!id || !userId) return
    this.starting.set(true)
    this.error.set(null)
    try {
      // Subscribe BEFORE starting so replay + live ticks both land.
      this.claim.claim([careerStream(userId, "Ingest", ingestRunUrn(id))])
      await apiPost("/api/ingest/start", { ingestId: id })
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.starting.set(false)
    }
  }

  goReview(): void {
    void this.router.navigate("/onboarding/review")
  }
}

export class CareerOnboarding extends KitElement<OnboardingVm> {
  protected createViewModel(ctx: KitContext): OnboardingVm {
    return new OnboardingVm(
      ctx.inject(IngestStoreToken),
      ctx.inject(PushToken),
      ctx.inject(RouterToken),
      ctx.inject(SessionToken),
      ctx.inject(I18nToken),
    )
  }

  /** The live run status — completed-with-zero-claims renders an HONEST cue (B1), not success. */
  private runStatus(vm: OnboardingVm, run: IngestRunView): TemplateResult {
    const done = run.status === "proposed" || run.status === "confirmed"
    const zeroClaims = done && run.claimCount === 0
    if (zeroClaims) {
      return html`<p class=${classMap({ "run-status": true, "is-failed": true })} role="alert" lang="de">
        ${vm.t("onboarding.zeroClaims")}
      </p>`
    }
    return html`
      <p class=${classMap({ "run-status": true, [`is-${run.status}`]: true })} lang="de">
        <span class="run-status-label" lang="de">${vm.t(STATUS_KEY[run.status])}</span>
        <span class="run-count chip" lang="de">${vm.t("onboarding.count.docs", { n: run.documents.length })}</span>
        ${done ? html`<span class="run-count chip" lang="de">${vm.t("onboarding.count.claims", { n: run.claimCount })}</span>` : nothing}
      </p>
      ${done
        ? html`<a href="/onboarding/review" data-act="nav" data-url="/onboarding/review" class="onboarding-review-link" lang="de">${vm.t("onboarding.toReview")}</a>`
        : nothing}
    `
  }

  protected render(vm: OnboardingVm): TemplateResult {
    const run = vm.runView
    const busy = vm.busyKind.value
    const uploaded = vm.uploaded.value
    const canStart = uploaded.length > 0 && !vm.starting.value
    const err = vm.error.value
    return html`
      <section class="onboarding surface" aria-labelledby="onboarding-title">
        <h1 id="onboarding-title" class="surface-title" lang="de">${vm.t("onboarding.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("onboarding.lead")}</p>

        <ul class="upload-grid">
          ${repeat(
            DOC_KINDS,
            (kind) => kind,
            (kind) => html`
              <li class=${classMap({ "upload-card": true, "is-pending": busy === kind })}>
                <span class="upload-kind" lang="de">${vm.t(KIND_LABEL[kind])}</span>
                <button data-act="upload-document" data-kind=${kind} ?disabled=${busy !== null} lang="de">
                  ${busy === kind ? vm.t("onboarding.uploading") : vm.t("onboarding.upload")}
                </button>
                <span class="upload-count chip" lang="de">${vm.t("onboarding.uploadedN", { n: uploaded.filter((u) => u.kind === kind).length })}</span>
              </li>
            `,
          )}
          <li class=${classMap({ "upload-card": true, "is-pending": busy === "portrait" })}>
            <span class="upload-kind" lang="de">${vm.t("onboarding.kind.portrait")}</span>
            <button data-act="upload-document" data-kind="portrait" data-portrait="true" ?disabled=${busy !== null} lang="de">
              ${busy === "portrait" ? vm.t("onboarding.uploading") : vm.t("onboarding.upload")}
            </button>
            <span class="upload-count chip" lang="de">${vm.t("onboarding.uploadedN", { n: uploaded.filter((u) => u.kind === "portrait").length })}</span>
          </li>
        </ul>
        <p class="onboarding-format-hint" lang="de">${vm.t("onboarding.formatHint")}</p>

        ${err ? html`<p class="onboarding-error" role="alert" lang="de">${vm.t("onboarding.error", { message: err })}</p>` : nothing}

        <div class="onboarding-actions">
          <button data-act="start-extraction" ?disabled=${!canStart} class=${classMap({ "is-pending": vm.starting.value })} lang="de">
            ${vm.starting.value ? vm.t("onboarding.starting") : vm.t("onboarding.start")}
          </button>
        </div>

        <section class="onboarding-run" aria-live="polite" aria-label=${vm.t("onboarding.runLabel")}>
          ${run ? this.runStatus(vm, run) : html`<p class="onboarding-run-empty" lang="de">${vm.t("onboarding.runEmpty")}</p>`}
        </section>
      </section>
    `
  }

  protected override actions(vm: OnboardingVm): ActionMap {
    return {
      "upload-document": ({ kind, portrait }) => void vm.pickAndUpload(kind ?? "other", portrait === "true"),
      "start-extraction": () => void vm.startExtraction(),
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url === "/onboarding/review") vm.goReview()
      },
    }
  }
}
